/**
 * PulseSparkline — Mini area chart for pulse tile trend visualization
 *
 * Renders a 40px Recharts AreaChart with gradient fill.
 * Healthy tiles: primary blue stroke + gradient.
 * Zero tiles: blurred ghost chart with aspirational upward trend (teaser).
 * Desktop: hover shows value tooltip. Mobile: tooltip hidden.
 */

import { AreaChart, Area, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import type { SparklinePoint } from "@/hooks/usePulseSparkline";

const canHover =
  typeof window !== "undefined" &&
  window.matchMedia("(hover: hover)").matches;

/** Ghost sparkline data — different shapes per metric so they don't look identical */
const GHOST_ORDERS: SparklinePoint[] = [
  { date: "", value: 2 },
  { date: "", value: 5 },
  { date: "", value: 3 },
  { date: "", value: 12 },
  { date: "", value: 9 },
  { date: "", value: 24 },
  { date: "", value: 18 },
  { date: "", value: 45 },
  { date: "", value: 38 },
  { date: "", value: 72 },
  { date: "", value: 65 },
  { date: "", value: 120 },
];

const GHOST_REVENUE: SparklinePoint[] = [
  { date: "", value: 1 },
  { date: "", value: 3 },
  { date: "", value: 8 },
  { date: "", value: 6 },
  { date: "", value: 18 },
  { date: "", value: 14 },
  { date: "", value: 35 },
  { date: "", value: 28 },
  { date: "", value: 58 },
  { date: "", value: 48 },
  { date: "", value: 85 },
  { date: "", value: 140 },
];

const GHOST_PRODUCTS: SparklinePoint[] = [
  { date: "", value: 1 },
  { date: "", value: 2 },
  { date: "", value: 2 },
  { date: "", value: 4 },
  { date: "", value: 5 },
  { date: "", value: 7 },
  { date: "", value: 8 },
  { date: "", value: 10 },
  { date: "", value: 13 },
  { date: "", value: 16 },
  { date: "", value: 20 },
  { date: "", value: 25 },
];

/** Metric color map — Apple system colors for ghost, primary blue for real data */
const GHOST_COLORS: Record<string, string> = {
  orders: "#007AFF",    // Apple blue
  revenue: "#AF52DE",   // Apple purple
  products: "#34C759",  // Apple green
  shopee: "#EE4D2D",    // Shopee orange
  lazada: "#171EB5",    // Lazada blue
  tiktok: "#000000",    // TikTok black
};

function SparklineTooltipContent({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ value?: number }>;
}) {
  if (!canHover || !active || payload?.[0]?.value == null) return null;
  const val = payload[0].value;
  return (
    <div className="bg-foreground text-background text-[10px] px-1.5 py-0.5 rounded-md shadow-sm whitespace-nowrap">
      {typeof val === "number" ? val.toLocaleString() : val}
    </div>
  );
}

interface PulseSparklineProps {
  data: SparklinePoint[];
  isZero?: boolean;
  /** Which metric — determines ghost shape and color. Default: "orders" */
  metric?: "orders" | "revenue" | "products" | "shopee" | "lazada" | "tiktok";
}

const GHOST_DATA_MAP: Record<string, SparklinePoint[]> = {
  orders: GHOST_ORDERS,
  revenue: GHOST_REVENUE,
  products: GHOST_PRODUCTS,
  shopee: GHOST_ORDERS,    // Reuse spiky order pattern
  lazada: GHOST_REVENUE,   // Reuse growing revenue pattern
  tiktok: GHOST_PRODUCTS,  // Reuse steady growth pattern
};

export function PulseSparkline({ data, isZero, metric = "orders" }: PulseSparklineProps) {
  const ghostData = GHOST_DATA_MAP[metric] ?? GHOST_ORDERS;
  const chartData = isZero ? ghostData : data;
  const ghostColor = GHOST_COLORS[metric] ?? "#007AFF";
  const strokeColor = isZero ? ghostColor : "#0051FD";
  const gradientId = isZero ? `sparkline-ghost-${metric}` : "sparkline-healthy";

  // Auto-scale Y axis: zoom in to show variation instead of anchoring at 0.
  // For flat data (e.g., active products = 135 every day), add ±15% padding
  // so the line renders at the midpoint, not glued to the ceiling.
  const values = chartData.map((d) => d.value);
  let sparklineMin = 0;
  let sparklineMax: number | string = "dataMax";
  if (values.length > 0) {
    const rawMin = Math.min(...values);
    const rawMax = Math.max(...values);
    const range = rawMax - rawMin;
    const pad = range > 0 ? range * 0.15 : rawMax * 0.1 || 1;
    sparklineMin = Math.max(0, Math.floor(rawMin - pad));
    sparklineMax = Math.ceil(rawMax + pad);
  }

  const ariaLabel = isZero
    ? "No data yet — example trend preview"
    : `Trend over ${data.length} days: ${data.map((d) => d.value).join(", ")}`;

  return (
    <div
      className="h-full w-full"
      role="img"
      aria-label={ariaLabel}
      style={isZero ? { opacity: 0.55, filter: "blur(0.5px)" } : undefined}
    >
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 0, right: 0, bottom: 6, left: 0 }}>
          <defs>
            <linearGradient id="sparkline-healthy" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#0051FD" stopOpacity={0.2} />
              <stop offset="100%" stopColor="#0051FD" stopOpacity={0} />
            </linearGradient>
            <linearGradient id={`sparkline-ghost-${metric}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={ghostColor} stopOpacity={0.45} />
              <stop offset="100%" stopColor={ghostColor} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <YAxis domain={[sparklineMin, sparklineMax]} hide />
          {!isZero && (
            <Tooltip
              content={<SparklineTooltipContent />}
              cursor={{ stroke: "#0051FD", strokeWidth: 1 }}
              wrapperStyle={{ zIndex: 10 }}
              position={{ y: -28 }}
              isAnimationActive={false}
            />
          )}
          <Area
            type="linear"
            dataKey="value"
            stroke={strokeColor}
            strokeWidth={1.5}
            fill={`url(#${gradientId})`}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
